import { LetterTypes, Sources } from '../generator/types';

import { ParagraphTemplate } from './blocks';
import { PricingMode } from './types';

export interface LetterFixedText {
  preBlocks: ParagraphTemplate[];
  postBlocks: ParagraphTemplate[];
}

export interface FixedTextInput {
  letterType: LetterTypes;
  selectedSources: Sources[];
  isJuvenile: boolean;
  hasRiders: boolean;
  // OTAF only: the drawer keeps higher and same pricing under one letter type.
  pricingMode?: PricingMode;
}

const has = (sources: Sources[], target: Sources): boolean =>
  sources.includes(target);

const hasAny = (sources: Sources[], targets: Sources[]): boolean =>
  sources.some(s => targets.includes(s));

export function getLetterFixedText(input: FixedTextInput): LetterFixedText {
  switch (input.letterType) {
    case LetterTypes.decline:
      return getDeclineFixedText(input);
    case LetterTypes.otaf:
      return input.pricingMode === 'same'
        ? getOtafSameFixedText(input)
        : getOtafHigherFixedText(input);
    case LetterTypes.otafSamePrice:
      return getOtafSameFixedText(input);
    case LetterTypes.reinstatementDecline:
      return getReinstatementDeclineFixedText(input);
    case LetterTypes.withdrawal:
      return getWithdrawalFixedText(input);
    default:
      return { preBlocks: [], postBlocks: [] };
  }
}

const INTRO_DECLINE: ParagraphTemplate = {
  text:
    'Thank you for applying for life insurance with {{carrierDisplayName}}. ' +
    'Your application is appreciated.',
  fields: [],
};

const REGRET_DECLINE_ADULT: ParagraphTemplate = {
  text:
    'The underwriting evaluation process of your application has been ' +
    'completed. We regret that we are unable to offer you insurance and ' +
    'cannot approve your application.',
  fields: [],
};

const REGRET_DECLINE_JUVENILE: ParagraphTemplate = {
  text:
    'We regret to inform you that the life insurance coverage for the ' +
    'proposed life insured can not be approved based on our underwriting ' +
    'review.',
  fields: [],
};

const ADDITIONAL_RIGHTS_LONG: ParagraphTemplate = {
  text:
    'In addition to the information given above, you have certain additional ' +
    'rights regarding the access, correction, and disclosure of the ' +
    'information contained in our files. The attached notice describes your ' +
    'rights to access the information obtained for our files and explains the ' +
    'steps you may take if you believe the information is incorrect. This ' +
    'information supplements the material already given to you at the time of ' +
    'your application for coverage.',
  fields: [],
};

const ADDITIONAL_RIGHTS_SHORT: ParagraphTemplate = {
  text:
    'The attached notice describes your rights to access the information ' +
    'obtained for our files and explains the steps you may take if you ' +
    'believe the information is incorrect. This information supplements the ' +
    'material already given to you at the time of your application for ' +
    'coverage.',
  fields: [],
};

const REFUND_DECLINE_JUVENILE: ParagraphTemplate = {
  text:
    'If applicable, a refund check for the initial premium deposit will ' +
    'follow in a separate envelope. There is no insurance in force under this ' +
    'application.',
  fields: [],
};

function getDeclineFixedText(input: FixedTextInput): LetterFixedText {
  const { selectedSources, isJuvenile } = input;

  const preBlocks: ParagraphTemplate[] = [
    INTRO_DECLINE,
    isJuvenile ? REGRET_DECLINE_JUVENILE : REGRET_DECLINE_ADULT,
  ];

  const postBlocks: ParagraphTemplate[] = [];

  // Additional rights paragraph — choose variant or skip
  // Source: DeclineLetterContent.tsx lines 48-68
  const useShortRights =
    isJuvenile &&
    hasAny(selectedSources, [
      Sources.mibAps,
      Sources.mibAppEti,
      Sources.mibRxOrMedClaimsMilliman,
      Sources.medClaimsAps,
    ]);
  const skipRights =
    has(selectedSources, Sources.mibLabPiQture) ||
    has(selectedSources, Sources.mibInsuranceLabs) ||
    (isJuvenile && has(selectedSources, Sources.criminalHistory)) ||
    (isJuvenile && has(selectedSources, Sources.rxOrMedClaimsMilliman));

  if (useShortRights) {
    postBlocks.push(ADDITIONAL_RIGHTS_SHORT);
  } else if (!skipRights) {
    postBlocks.push(ADDITIONAL_RIGHTS_LONG);
  }

  // Refund paragraph — juvenile only, excluding certain sources
  // Source: DeclineLetterContent.tsx lines 69-80
  const refundExcluded = [
    Sources.mibAps,
    Sources.mibAppEti,
    Sources.mibRxOrMedClaimsMilliman,
    Sources.mibInsuranceLabs,
    Sources.mibLabPiQture,
    Sources.medClaimsAps,
    Sources.labPiQture,
    Sources.mvr,
  ];
  if (isJuvenile && !hasAny(selectedSources, refundExcluded)) {
    postBlocks.push(REFUND_DECLINE_JUVENILE);
  }

  return { preBlocks, postBlocks };
}

const INTRO_OTAF: ParagraphTemplate = {
  text:
    'Thank you for applying for life insurance with {{carrierDisplayName}}. ' +
    'This letter is for your information only.',
  fields: [],
};

const OTAF_HEADER_ADULT: ParagraphTemplate = {
  text:
    'We are letting you know your life insurance coverage has been approved ' +
    'at a rate higher than the rate you were initially quoted when you first ' +
    'applied. There will be no further pricing changes as a result of this ' +
    'letter.',
  fields: [],
};

const OTAF_HEADER_JUVENILE_FINANCIAL: ParagraphTemplate = {
  text:
    'We are letting you know that life insurance coverage of the proposed ' +
    'life insured has been approved at a different coverage amount than you ' +
    'first applied for. There will be no further pricing changes as a result ' +
    'of this letter.',
  fields: [],
};

const OTAF_HEADER_JUVENILE: ParagraphTemplate = {
  text:
    'We are informing you that the life insurance coverage for the proposed ' +
    'life insured has been approved. However, based on our underwriting ' +
    'review, the policy has been issued at a premium rate higher than what ' +
    'was initially quoted at the time of application. Please note that there ' +
    'will be no further pricing changes resulting from this notification.',
  fields: [],
};

const ACTIVATION_POLICY: ParagraphTemplate = {
  text:
    'If you have activated coverage, no action is required. Your policy will ' +
    'continue to remain in force as long as you continue to pay your current ' +
    'premiums as scheduled. If you have not activated coverage, you can do ' +
    'so by paying your initial premium. Once your policy has been activated, ' +
    'your premium payment amounts will remain as shown on your policy ' +
    'schedule.',
  fields: [],
};

const RIDER_CLAUSE: ParagraphTemplate = {
  text: 'Additionally, we are approving the policy without the {{riders}}.',
  fields: [],
};

const REASON_TO_KNOW_RATE: ParagraphTemplate = {
  text:
    'You have the right to know the specific reason why your application was ' +
    'approved with a different premium rate. This may be requested by ' +
    'emailing Ethos at ' +
    '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
    'representatives will be glad to help.',
  fields: [],
};

const REASON_TO_KNOW_COVERAGE: ParagraphTemplate = {
  text:
    'You have the right to know the specific reason why your application was ' +
    'approved with a different coverage amount. This may be requested by ' +
    'emailing Ethos at ' +
    '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
    'representatives will be glad to help.',
  fields: [],
};

const OTAF_RIGHTS_NOTICE: ParagraphTemplate = {
  text:
    'The attached notice describes your rights to access the additional ' +
    'information obtained for our files and explains the steps you may take ' +
    'if you believe the information is incorrect. This information ' +
    'supplements the material already given to you at the time of your ' +
    'application for coverage.',
  fields: [],
};

function getOtafHigherFixedText(input: FixedTextInput): LetterFixedText {
  const { selectedSources, isJuvenile, hasRiders } = input;

  const preBlocks: ParagraphTemplate[] = [INTRO_OTAF];

  // "We are letting you know" variant
  // Source: OtafLetterContent.tsx lines 31-54
  if (isJuvenile && has(selectedSources, Sources.financial)) {
    preBlocks.push(OTAF_HEADER_JUVENILE_FINANCIAL);
  } else if (isJuvenile) {
    preBlocks.push(OTAF_HEADER_JUVENILE);
  } else {
    preBlocks.push(OTAF_HEADER_ADULT);
  }

  // Juvenile pre-decision activation policy
  // Source: OtafLetterContent.tsx lines 55-67
  // Render only if juvenile AND none of selected sources is eti/labPiQture/mibLabPiQture
  const skipPreActivation = hasAny(selectedSources, [
    Sources.eti,
    Sources.rxEti,
    Sources.labPiQture,
    Sources.mibInsuranceLabs,
    Sources.mibLabPiQture,
  ]);
  if (isJuvenile && !skipPreActivation) {
    preBlocks.push(ACTIVATION_POLICY);
  }

  const postBlocks: ParagraphTemplate[] = [];

  if (hasRiders) {
    postBlocks.push(RIDER_CLAUSE);
  }

  // Post-decision activation policy
  // Source: OtafLetterContent.tsx lines 81-91
  // Render if (!juvenile && no selected source is mibLabPiQture) || (juvenile && eti selected)
  const showPostActivation =
    (!isJuvenile && !has(selectedSources, Sources.mibLabPiQture)) ||
    (isJuvenile && has(selectedSources, Sources.eti));
  if (showPostActivation) {
    postBlocks.push(ACTIVATION_POLICY);
  }

  // Juvenile reason-to-know
  // Source: OtafLetterContent.tsx lines 92-108
  const reasonToKnowExcluded = [
    Sources.labPiQture,
    Sources.mibLabPiQture,
    Sources.mvr,
    Sources.rxOrMedClaimsMilliman,
    Sources.rxOrMedClaimsExamOne,
    Sources.rxEti,
    Sources.mibInsuranceLabs,
    Sources.trl,
    Sources.custom,
  ];
  if (isJuvenile && !hasAny(selectedSources, reasonToKnowExcluded)) {
    postBlocks.push(
      has(selectedSources, Sources.financial)
        ? REASON_TO_KNOW_COVERAGE
        : REASON_TO_KNOW_RATE,
    );
  }

  postBlocks.push(OTAF_RIGHTS_NOTICE);

  return { preBlocks, postBlocks };
}

// Same pricing, rider(s) declined, no reason block picked: the sentence a reason
// block would otherwise open with. The juvenile wording is the one in the
// North American juvenile rider-decline letter.
const OTAF_SAME_RIDERS_ONLY_ADULT: ParagraphTemplate = {
  text:
    'We are letting you know your life insurance coverage has been approved. ' +
    'However, we are approving the policy without the {{riders}}.',
  fields: [],
};

const OTAF_SAME_RIDERS_ONLY_JUVENILE: ParagraphTemplate = {
  text:
    'We are letting you know that the life insurance coverage for the ' +
    'proposed life insured has been approved. However, we are approving the ' +
    'policy without the {{riders}}.',
  fields: [],
};

function getOtafSameFixedText(input: FixedTextInput): LetterFixedText {
  // Source: OtafSamePriceLetterContent.tsx — all wrapper text is unconditional,
  // except the no-block sentence (the old popup always has one source picked).
  const preBlocks: ParagraphTemplate[] = [INTRO_OTAF];
  if (input.selectedSources.length === 0 && input.hasRiders) {
    preBlocks.push(
      input.isJuvenile
        ? OTAF_SAME_RIDERS_ONLY_JUVENILE
        : OTAF_SAME_RIDERS_ONLY_ADULT,
    );
  }
  const postBlocks: ParagraphTemplate[] = [
    ACTIVATION_POLICY,
    OTAF_RIGHTS_NOTICE,
  ];
  return { preBlocks, postBlocks };
}

const INTRO_WITHDRAWAL_ADULT: ParagraphTemplate = {
  text:
    'Thank you for your application for life insurance with ' +
    '{{carrierDisplayName}}.',
  fields: [],
};

const INTRO_WITHDRAWAL_JUVENILE: ParagraphTemplate = {
  text:
    'Thank you for applying for life insurance with {{carrierDisplayName}}. ' +
    'This letter is for your information only.',
  fields: [],
};

const REAPPLY_WITHDRAWAL_ADULT: ParagraphTemplate = {
  text:
    'Should you like to be reconsidered for coverage, a new application will ' +
    'be required. Thus, you will need to reapply.',
  fields: [],
};

const REAPPLY_WITHDRAWAL_JUVENILE: ParagraphTemplate = {
  text:
    'Should you like to be reconsidered for coverage on the proposed life ' +
    'insured, a new application will be required. Thus, you will need to ' +
    'reapply.',
  fields: [],
};

function getWithdrawalFixedText(input: FixedTextInput): LetterFixedText {
  // Source: WithdrawalLetterContent.tsx — every reason ends with the reapply
  // line except Unresolved MIB. Custom gets it too (decided for the drawer).
  const { selectedSources, isJuvenile } = input;
  const preBlocks: ParagraphTemplate[] = [
    isJuvenile ? INTRO_WITHDRAWAL_JUVENILE : INTRO_WITHDRAWAL_ADULT,
  ];
  const postBlocks: ParagraphTemplate[] = [];
  if (
    selectedSources.length > 0 &&
    !has(selectedSources, Sources.unresolvedMib)
  ) {
    postBlocks.push(
      isJuvenile ? REAPPLY_WITHDRAWAL_JUVENILE : REAPPLY_WITHDRAWAL_ADULT,
    );
  }
  return { preBlocks, postBlocks };
}

const INTRO_REINSTATEMENT_DECLINE: ParagraphTemplate = {
  text:
    'Thank you for applying for life insurance with {{carrierDisplayName}}. ' +
    'Your application is appreciated.',
  fields: [],
};

const REGRET_REINSTATEMENT: ParagraphTemplate = {
  text:
    'The underwriting evaluation process of your reinstatement request has ' +
    'been completed. We regret that we are unable to reinstate your policy.',
  fields: [],
};

const REINSTATEMENT_ADDITIONAL_RIGHTS: ParagraphTemplate = {
  text:
    'In addition to the information given above, you have certain additional ' +
    'rights regarding the access, correction, and disclosure of the ' +
    'information contained in our files. The attached notice describes your ' +
    'rights to access the information obtained for our files and explains ' +
    'the steps you may take if you believe the information is incorrect. ' +
    'This information supplements the material already given to you at the ' +
    'time of your application for coverage.',
  fields: [],
};

const REINSTATEMENT_REFUND: ParagraphTemplate = {
  text:
    'If applicable, a refund check for the premium deposit will follow in a ' +
    'separate envelope. There is no insurance in force under this ' +
    'application.',
  fields: [],
};

function getReinstatementDeclineFixedText(
  _input: FixedTextInput,
): LetterFixedText {
  // Source: ReinstatementDeclineContent.tsx — all wrapper text is unconditional
  const preBlocks: ParagraphTemplate[] = [
    INTRO_REINSTATEMENT_DECLINE,
    REGRET_REINSTATEMENT,
  ];
  const postBlocks: ParagraphTemplate[] = [
    REINSTATEMENT_ADDITIONAL_RIGHTS,
    REINSTATEMENT_REFUND,
  ];
  return { preBlocks, postBlocks };
}
